import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Avatar,
  Chip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Divider,
  useTheme,
  alpha,
  CircularProgress,
  Snackbar,
  InputAdornment,
  Pagination,
  Badge,
  Card,
  CardContent,
  Tab,
  Tabs,
  Collapse
} from '@mui/material';
import {
  Store as StoreIcon,
  Campaign as CampaignIcon,
  Receipt as ReceiptIcon,
  CalendarToday as CalendarIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  ArrowBack as BackIcon,
  FilterList as FilterIcon,
  CheckCircle as CheckCircleIcon,
  Visibility as VisibilityIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Flag as FlagIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  AccountBalance as AccountBalanceIcon,
  Payment as PaymentIcon,
  Business as BusinessIcon,
  ShoppingCart as ProductIcon,
  ReceiptLong as LedgerIcon,
  Summarize as SummaryIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  DateRange as DateRangeIcon,
  AccessTime as TimeIcon,
  ProductionQuantityLimits as NoProductIcon,
  ConfirmationNumber as VoucherIcon,
  CheckCircle as RedeemedIcon,
  Cancel as UnusedIcon,
  VolunteerActivism as DonorIcon,
  Favorite as HeartIcon,
  AttachMoney as MoneyIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import { format } from 'date-fns';

// Ionic Imports
import { IonContent, IonPage } from '@ionic/react';
import {
  getAllDonorsReport,
  getDonorCampaignsReport,
  getDonorPaymentSummaryReport,
  getDonorPaymentLedgerReport,
  getDonorVoucherTotals
} from '../../../api/analytics/analytics';

// Types
interface Donor {
  user_id: string;
  donor_name: string;
  email: string;
  donor_phone: string;
  total_campaigns: number;
  campaigns: string;
}

interface ParsedCampaign {
  name: string;
  budget: number;
  start_date: string;
  end_date: string;
  description?: string;
  state?: string;
  local_government?: string;
  reach?: number;
}

interface CampaignDetail {
  campaign_id: string;
  campaign_name: string;
  campaign_description: string;
  start_date: string;
  end_date: string;
  total_vouchers_purchased: number | null;
  total_amount_paid: string | null;
}

interface GroupedCampaign {
  campaign_id: string;
  campaign_name: string;
  campaign_description: string;
  start_date: string;
  end_date: string;
  vouchers: VoucherDetail[];
}

interface VoucherDetail {
  voucher_id: string;
  voucher_code: string;
  amount: string;
  status: string;
  purchase_date: string;
  redemption_date: string | null;
  business_name: string | null;
}

interface CampaignPayment {
  campaign_id: string;
  campaign_name: string;
  start_date: string;
  end_date: string;
  total_vouchers: number;
  total_payments: number;
  used_vouchers?: number;
  remaining_vouchers?: number;
}

interface PaymentLedger {
  id: number;
  walletID: string;
  user_id: string;
  campaign_id: string | null;
  campaign_name: string | null;
  shopID: string;
  drValue: string;
  crValue: string;
  amount: number;
  desp: string;
  dateCreated: string;
  last_updated: string;
}

interface PaymentSummary {
  total_payments: number;
  campaigns: CampaignPayment[];
}

interface VoucherTotals {
  total_vouchers: number;
  redeemed_vouchers: number;
  remaining_vouchers: number;
}

interface FilterOptions {
  search: string;
  dateRange: string;
  status: string;
}

// Tab Panel Component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`donor-tabpanel-${index}`}
      aria-labelledby={`donor-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Donor Info Card Component
const DonorInfoCard = ({ donor }: { donor: Donor | null }) => {
  const theme = useTheme();

  if (!donor) return null;

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
            <DonorIcon />
          </Avatar>
          <Typography variant="h6" fontWeight={600}>
            Donor Information
          </Typography>
        </Stack>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.02),
                borderColor: alpha(theme.palette.primary.main, 0.1)
              }}
            >
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                <PersonIcon sx={{ fontSize: 14, mr: 0.5 }} />
                Donor Name
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body1" fontWeight={600}>
                    {donor.donor_name}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: alpha(theme.palette.info.main, 0.02),
                borderColor: alpha(theme.palette.info.main, 0.1)
              }}
            >
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                <EmailIcon sx={{ fontSize: 14, mr: 0.5 }} />
                Email
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2">
                    {donor.email}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: alpha(theme.palette.success.main, 0.02),
                borderColor: alpha(theme.palette.success.main, 0.1)
              }}
            >
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                <PhoneIcon sx={{ fontSize: 14, mr: 0.5 }} />
                Phone
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body1" fontWeight={600}>
                    {donor.donor_phone || 'No phone provided'}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

// Main Component
const DonorReport = () => {
  const theme = useTheme();
  const history = useHistory();

  // Data states
  const [donors, setDonors] = useState<Donor[]>([]);
  const [selectedDonorId, setSelectedDonorId] = useState<string>('');
  const [selectedDonor, setSelectedDonor] = useState<Donor | null>(null);
  const [parsedCampaigns, setParsedCampaigns] = useState<ParsedCampaign[]>([]);

  // Tab data states
  const [campaigns, setCampaigns] = useState<CampaignDetail[]>([]);
  const [groupedCampaigns, setGroupedCampaigns] = useState<GroupedCampaign[]>([]);
  const [paymentSummary, setPaymentSummary] = useState<PaymentSummary | null>(null);
  const [paymentLedger, setPaymentLedger] = useState<PaymentLedger[]>([]);
  const [voucherTotals, setVoucherTotals] = useState<VoucherTotals | null>(null);
  const [expandedCampaign, setExpandedCampaign] = useState<string | null>(null);

  // Filter states
  const [filters, setFilters] = useState<FilterOptions>({
    search: '',
    dateRange: 'all',
    status: 'all'
  });

  // UI states
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState({
    donors: false,
    campaigns: false,
    payments: false,
    ledger: false,
    vouchers: false
  });
  const [refreshing, setRefreshing] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Fetch donors on initial load
  useEffect(() => {
    fetchDonors();
  }, []);

  // Update selected donor when donors load or selection changes
  useEffect(() => {
    if (selectedDonorId && donors.length > 0) {
      const donor = donors.find(d => d.user_id === selectedDonorId);
      setSelectedDonor(donor || null);

      // Parse campaigns string when donor is selected
      if (donor && donor.campaigns) {
        const parsed = parseCampaignsString(donor.campaigns);
        setParsedCampaigns(parsed);
      } else {
        setParsedCampaigns([]);
      }
    } else {
      setSelectedDonor(null);
      setParsedCampaigns([]);
    }
  }, [selectedDonorId, donors]);

  // Parse the campaigns string from the API response
  const parseCampaignsString = (campaignsStr: string): ParsedCampaign[] => {
    if (!campaignsStr) return [];

    const campaigns: ParsedCampaign[] = [];
    const campaignEntries = campaignsStr.split('||');

    campaignEntries.forEach(entry => {
      if (entry.trim() === '') return;

      try {
        // Parse each campaign entry
        const nameMatch = entry.match(/Campaign:\s*([^|]+)/);
        const budgetMatch = entry.match(/Budget:\s*([\d,.]+)/);
        const startMatch = entry.match(/Start:\s*([^\s|]+)/);
        const endMatch = entry.match(/End:\s*([^\s|]+)/);
        const descriptionMatch = entry.match(/Description:\s*([^|]+)/);
        const stateMatch = entry.match(/State:\s*([^|]+)/);
        const lgMatch = entry.match(/Local government:\s*([^|]+)/);
        const reachMatch = entry.match(/Reach:\s*(\d+)/);

        if (nameMatch && budgetMatch && startMatch && endMatch) {
          campaigns.push({
            name: nameMatch[1].trim(),
            budget: parseFloat(budgetMatch[1].replace(/,/g, '')),
            start_date: startMatch[1].trim(),
            end_date: endMatch[1].trim(),
            description: descriptionMatch ? descriptionMatch[1].trim() : '',
            state: stateMatch ? stateMatch[1].trim() : '',
            local_government: lgMatch ? lgMatch[1].trim() : '',
            reach: reachMatch ? parseInt(reachMatch[1]) : 0
          });
        }
      } catch (error) {
        console.error('Error parsing campaign entry:', entry, error);
      }
    });

    return campaigns;
  };

  // Fetch all donor data when donor is selected
  useEffect(() => {
    if (selectedDonorId) {
      fetchDonorCampaigns();
      fetchDonorPaymentSummary();
      fetchDonorPaymentLedger();
      fetchDonorVoucherTotals();
    } else {
      // Reset all data
      setCampaigns([]);
      setGroupedCampaigns([]);
      setPaymentSummary(null);
      setPaymentLedger([]);
      setVoucherTotals(null);
    }
  }, [selectedDonorId]);

  // Group campaigns and their vouchers when campaigns data changes
  useEffect(() => {
    if (campaigns.length > 0) {
      const grouped = groupCampaigns(campaigns);
      setGroupedCampaigns(grouped);
    } else {
      setGroupedCampaigns([]);
    }
  }, [campaigns]);

  const groupCampaigns = (campaignList: CampaignDetail[]): GroupedCampaign[] => {
    const campaignMap = new Map<string, GroupedCampaign>();

    campaignList.forEach(item => {
      if (!campaignMap.has(item.campaign_id)) {
        campaignMap.set(item.campaign_id, {
          campaign_id: item.campaign_id,
          campaign_name: item.campaign_name,
          campaign_description: item.campaign_description,
          start_date: item.start_date,
          end_date: item.end_date,
          vouchers: []
        });
      }
    });

    return Array.from(campaignMap.values());
  };

  const fetchDonors = async () => {
    setLoading(prev => ({ ...prev, donors: true }));
    try {
      const response = await getAllDonorsReport();
      // The response structure from your API
      const data = response?.data || response;
      const donorsArray = Array.isArray(data) ? data : (data ? [data] : []);
      setDonors(donorsArray);
      setError(null);

      if (donorsArray.length === 0) {
        showNotification('No donors found', 'info');
      }
    } catch (err) {
      console.error('Error fetching donors:', err);
      setError("Failed to load donors");
      showNotification("Failed to load donors", 'error');
    } finally {
      setLoading(prev => ({ ...prev, donors: false }));
    }
  };

  const fetchDonorCampaigns = async () => {
    if (!selectedDonorId) return;

    setLoading(prev => ({ ...prev, campaigns: true }));
    try {
      const response = await getDonorCampaignsReport(selectedDonorId);
      const data = response?.data?.data || response?.data || response;
      setCampaigns(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching campaigns:', err);
      showNotification('Failed to load campaigns', 'error');
    } finally {
      setLoading(prev => ({ ...prev, campaigns: false }));
    }
  };

  const fetchDonorPaymentSummary = async () => {
    if (!selectedDonorId) return;

    setLoading(prev => ({ ...prev, payments: true }));
    try {
      const response = await getDonorPaymentSummaryReport(selectedDonorId);
      const data = response?.data?.data || response?.data || response;
      setPaymentSummary(data || null);
    } catch (err) {
      console.error('Error fetching payment summary:', err);
      showNotification('Failed to load payment summary', 'error');
    } finally {
      setLoading(prev => ({ ...prev, payments: false }));
    }
  };

  const fetchDonorPaymentLedger = async () => {
    if (!selectedDonorId) return;

    setLoading(prev => ({ ...prev, ledger: true }));
    try {
      const response = await getDonorPaymentLedgerReport(selectedDonorId);
      const data = response?.data?.data || response?.data || response;
      setPaymentLedger(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching payment ledger:', err);
      showNotification('Failed to load payment ledger', 'error');
    } finally {
      setLoading(prev => ({ ...prev, ledger: false }));
    }
  };

  const fetchDonorVoucherTotals = async () => {
    if (!selectedDonorId) return;

    setLoading(prev => ({ ...prev, vouchers: true }));
    try {
      const response = await getDonorVoucherTotals(selectedDonorId);
      const data = response?.data?.data || response?.data || response;
      setVoucherTotals(data || null);
    } catch (err) {
      console.error('Error fetching voucher totals:', err);
      showNotification('Failed to load voucher totals', 'error');
    } finally {
      setLoading(prev => ({ ...prev, vouchers: false }));
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDonors();
    if (selectedDonorId) {
      await fetchDonorCampaigns();
      await fetchDonorPaymentSummary();
      await fetchDonorPaymentLedger();
      await fetchDonorVoucherTotals();
    }
    setRefreshing(false);
    showNotification('Data refreshed successfully', 'success');
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const toggleCampaignExpand = (campaignId: string) => {
    setExpandedCampaign(expandedCampaign === campaignId ? null : campaignId);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return dateString;
    }
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
    } catch {
      return dateString;
    }
  };

  const formatAmount = (amount?: number | string) => {
    if (amount === undefined || amount === null || amount === '') return '₦0.00';
    return `₦${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatBudget = (budget?: number) => {
    if (budget === undefined || budget === null) return '₦0.00';
    return `₦${budget.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatNumber = (num?: number) => {
    if (num === undefined || num === null) return '0';
    return num.toLocaleString();
  };

  const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Calculate stats
  const stats = {
    totalCampaigns: selectedDonor?.total_campaigns || 0,
    parsedCampaignsCount: parsedCampaigns.length,
    totalVouchers: voucherTotals?.total_vouchers || 0,
    totalRedeemed: voucherTotals?.redeemed_vouchers || 0,
    totalRemaining: voucherTotals?.remaining_vouchers || 0,
    totalPayments: paymentSummary?.total_payments || 0,
    totalTransactions: paymentLedger.length
  };

  // Calculate redemption percentage
  const redemptionPercentage = stats.totalVouchers > 0
    ? ((stats.totalRedeemed / stats.totalVouchers) * 100).toFixed(1)
    : '0';

  // Calculate total budget from parsed campaigns
  const totalBudget = parsedCampaigns.reduce((sum, campaign) => sum + campaign.budget, 0);

  // Pagination for donors table
  const filteredDonors = donors.filter(d => {
    if (!filters.search) return true;
    const searchLower = filters.search.toLowerCase();
    return d.donor_name?.toLowerCase().includes(searchLower) ||
      d.email?.toLowerCase().includes(searchLower) ||
      d.donor_phone?.includes(searchLower);
  });

  const pageCount = Math.ceil(filteredDonors.length / rowsPerPage);
  const paginatedDonors = filteredDonors.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const activeFilterCount = filters.search ? 1 : 0;

  return (
    <IonPage>
      <IonContent>
        <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh' }}>

          {/* Header */}
          <Paper
            elevation={0}
            sx={{
              borderBottom: `1px solid ${theme.palette.divider}`,
              position: 'sticky',
              top: 0,
              zIndex: 10,
              bgcolor: 'background.paper'
            }}
          >
            <Container maxWidth="xl">
              <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 2 }}>
                <IconButton onClick={() => history.goBack()} edge="start">
                  <BackIcon />
                </IconButton>
                <Avatar
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main
                  }}
                >
                  <DonorIcon />
                </Avatar>
                <Box flex={1}>
                  <Typography variant="h5" fontWeight={600}>
                    Donor Report
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    View donor campaigns, vouchers and payment details
                  </Typography>
                </Box>
                <Tooltip title="Refresh">
                  <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={handleRefresh}
                    disabled={refreshing}
                    size="small"
                  >
                    Refresh
                  </Button>
                </Tooltip>
              </Stack>
            </Container>
          </Paper>

          <Container maxWidth="xl" sx={{ py: 3 }}>

            {/* Donor Selection */}
            <Paper sx={{ mb: 3, p: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Select Donor</InputLabel>
                    <Select
                      value={selectedDonorId}
                      label="Select Donor"
                      onChange={(e) => setSelectedDonorId(e.target.value)}
                      disabled={loading.donors}
                    >
                      <MenuItem value="">
                        <em>Select a donor</em>
                      </MenuItem>
                      {donors.map((donor) => (
                        <MenuItem key={donor.user_id} value={donor.user_id}>
                          <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                            <DonorIcon fontSize="small" color="primary" />
                            <Typography sx={{ flex: 1 }}>{donor.donor_name}</Typography>
                            <Chip
                              size="small"
                              label={`${donor.total_campaigns} campaigns`}
                              variant="outlined"
                              sx={{ ml: 1 }}
                            />
                          </Stack>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>

            {selectedDonorId && (
              <>
                {/* Donor Info */}
                {selectedDonor && <DonorInfoCard donor={selectedDonor} />}

                {/* Stats Cards */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Card>
                      <CardContent>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main }}>
                            <CampaignIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Total Campaigns
                            </Typography>
                            <Typography variant="h4">
                              {stats.totalCampaigns}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6} md={2.4}>
                    <Card>
                      <CardContent>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.main }}>
                            <VoucherIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Total Vouchers
                            </Typography>
                            <Typography variant="h4">
                              {loading.vouchers ? <CircularProgress size={24} /> : formatNumber(stats.totalVouchers)}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6} md={2.4}>
                    <Card>
                      <CardContent>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.main }}>
                            <RedeemedIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Redeemed Vouchers
                            </Typography>
                            <Typography variant="h4" color="success.main">
                              {loading.vouchers ? <CircularProgress size={24} /> : formatNumber(stats.totalRedeemed)}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6} md={2.4}>
                    <Card>
                      <CardContent>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                            <MoneyIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Total Spent
                            </Typography>
                            <Typography variant="h5" fontWeight={700} color="primary.main">
                              {loading.payments ? <CircularProgress size={24} /> : formatAmount(stats.totalPayments)}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6} md={2.4}>
                    <Card>
                      <CardContent>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.main }}>
                            <ReceiptIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Transactions
                            </Typography>
                            <Typography variant="h4">
                              {stats.totalTransactions}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Tabs */}
                <Paper sx={{ mb: 3 }}>
                  <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
                    <Tabs value={tabValue} onChange={handleTabChange}>
                      <Tab
                        icon={<CampaignIcon />}
                        label="Campaigns & Vouchers"
                        iconPosition="start"
                      />
                      <Tab
                        icon={<SummaryIcon />}
                        label="Voucher Summary"
                        iconPosition="start"
                      />
                      <Tab
                        icon={<LedgerIcon />}
                        label="Payment Ledger"
                        iconPosition="start"
                      />
                    </Tabs>
                  </Box>

                  {/* Campaigns & Vouchers Tab */}
                  <TabPanel value={tabValue} index={0}>
                    {parsedCampaigns.length > 0 ? (
                      <>
                        {/* Campaign Summary Cards */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                          <Grid item xs={12} md={4}>
                            <Card sx={{ bgcolor: alpha(theme.palette.info.main, 0.05) }}>
                              <CardContent>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main }}>
                                    <CampaignIcon />
                                  </Avatar>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">
                                      Total Campaigns
                                    </Typography>
                                    <Typography variant="h5" fontWeight={700}>
                                      {parsedCampaigns.length}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </CardContent>
                            </Card>
                          </Grid>

                          <Grid item xs={12} md={4}>
                            <Card sx={{ bgcolor: alpha(theme.palette.success.main, 0.05) }}>
                              <CardContent>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.main }}>
                                    <MoneyIcon />
                                  </Avatar>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">
                                      Total Budget Committed
                                    </Typography>
                                    <Typography variant="h5" fontWeight={700} color="success.main">
                                      {formatBudget(totalBudget)}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </CardContent>
                            </Card>
                          </Grid>

                          <Grid item xs={12} md={4}>
                            <Card sx={{ bgcolor: alpha(theme.palette.warning.main, 0.05) }}>
                              <CardContent>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.main }}>
                                    <DateRangeIcon />
                                  </Avatar>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">
                                      Active Campaigns
                                    </Typography>
                                    <Typography variant="h5" fontWeight={700} color="warning.main">
                                      {parsedCampaigns.filter(c => new Date(c.end_date) >= new Date()).length}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </CardContent>
                            </Card>
                          </Grid>
                        </Grid>

                        {/* Campaigns Table */}
                        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                          Campaign Details
                        </Typography>

                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Campaign Name</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell align="right">Budget</TableCell>
                                <TableCell>Start Date</TableCell>
                                <TableCell>End Date</TableCell>
                                <TableCell>State</TableCell>
                                <TableCell>LGA</TableCell>
                                <TableCell align="right">Reach</TableCell>
                                <TableCell>Status</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {parsedCampaigns.map((campaign, index) => {
                                const isActive = new Date(campaign.end_date) >= new Date();

                                return (
                                  <TableRow key={index} hover>
                                    <TableCell>
                                      <Typography variant="body2" fontWeight={600}>
                                        {campaign.name}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Tooltip title={campaign.description || ''}>
                                        <Typography variant="body2" sx={{
                                          maxWidth: 200,
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap'
                                        }}>
                                          {campaign.description || 'N/A'}
                                        </Typography>
                                      </Tooltip>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography variant="body2" fontWeight={600} color="success.main">
                                        {formatBudget(campaign.budget)}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2">
                                        {formatDate(campaign.start_date)}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2">
                                        {formatDate(campaign.end_date)}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2">
                                        {campaign.state || 'N/A'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2">
                                        {campaign.local_government || 'N/A'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography variant="body2">
                                        {campaign.reach || 0}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        size="small"
                                        label={isActive ? 'Active' : 'Ended'}
                                        color={isActive ? 'success' : 'default'}
                                        variant="outlined"
                                      />
                                    </TableCell>
                                  </TableRow>
                                );
                              })}

                              {/* Totals Row */}
                              <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                                <TableCell colSpan={2}>
                                  <Typography variant="subtitle2" fontWeight={700}>
                                    TOTALS
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="subtitle2" fontWeight={700} color="success.main">
                                    {formatBudget(totalBudget)}
                                  </Typography>
                                </TableCell>
                                <TableCell colSpan={4}></TableCell>
                                <TableCell align="right">
                                  <Typography variant="subtitle2" fontWeight={700}>
                                    {parsedCampaigns.reduce((sum, c) => sum + (c.reach || 0), 0)}
                                  </Typography>
                                </TableCell>
                                <TableCell></TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <CampaignIcon sx={{ fontSize: 48, color: theme.palette.grey[300], mb: 2 }} />
                        <Typography color="text.secondary">
                          No campaign data found for this donor
                        </Typography>
                      </Box>
                    )}
                  </TabPanel>

                  {/* Voucher Summary Tab */}
                  <TabPanel value={tabValue} index={1}>
                    {loading.payments ? (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : !paymentSummary || paymentSummary.campaigns?.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <VoucherIcon sx={{ fontSize: 48, color: theme.palette.grey[300], mb: 2 }} />
                        <Typography color="text.secondary">
                          No voucher records found
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        {/* Voucher Summary Cards */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                          <Grid item xs={12} md={4}>
                            <Card sx={{ bgcolor: alpha(theme.palette.info.main, 0.05) }}>
                              <CardContent>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main }}>
                                    <VoucherIcon />
                                  </Avatar>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">
                                      Total Vouchers
                                    </Typography>
                                    <Typography variant="h5" fontWeight={700}>
                                      {formatNumber(stats.totalVouchers)}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </CardContent>
                            </Card>
                          </Grid>

                          <Grid item xs={12} md={4}>
                            <Card sx={{ bgcolor: alpha(theme.palette.success.main, 0.05) }}>
                              <CardContent>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.main }}>
                                    <RedeemedIcon />
                                  </Avatar>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">
                                      Redeemed Vouchers
                                    </Typography>
                                    <Typography variant="h5" fontWeight={700} color="success.main">
                                      {formatNumber(stats.totalRedeemed)}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </CardContent>
                            </Card>
                          </Grid>

                          <Grid item xs={12} md={4}>
                            <Card sx={{ bgcolor: alpha(theme.palette.warning.main, 0.05) }}>
                              <CardContent>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.main }}>
                                    <UnusedIcon />
                                  </Avatar>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">
                                      Unused Vouchers
                                    </Typography>
                                    <Typography variant="h5" fontWeight={700} color="warning.main">
                                      {formatNumber(stats.totalRemaining)}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </CardContent>
                            </Card>
                          </Grid>
                        </Grid>

                        {/* Redemption Rate Card */}
                        <Card sx={{ mb: 3, bgcolor: alpha(theme.palette.primary.main, 0.03) }}>
                          <CardContent>
                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                              <Stack direction="row" alignItems="center" spacing={2}>
                                <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                                  <SummaryIcon />
                                </Avatar>
                                <Box>
                                  <Typography variant="body2" color="text.secondary">
                                    Overall Redemption Rate
                                  </Typography>
                                  <Typography variant="h3" fontWeight={700} color="primary.main">
                                    {redemptionPercentage}%
                                  </Typography>
                                </Box>
                              </Stack>
                              <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="body2" color="text.secondary">
                                  {formatNumber(stats.totalRedeemed)} redeemed out of {formatNumber(stats.totalVouchers)} total
                                </Typography>
                              </Box>
                            </Stack>
                          </CardContent>
                        </Card>

                        {/* Campaign-wise Summary */}
                        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                          Campaign-wise Breakdown
                        </Typography>

                        {paymentSummary.campaigns?.map((campaign) => {
                          // Calculate campaign-specific redemption percentage
                          const campaignRedemptionPercentage = campaign.total_vouchers > 0
                            ? ((campaign.used_vouchers || 0) / campaign.total_vouchers * 100).toFixed(1)
                            : '0';

                          return (
                            <Card key={campaign.campaign_id} variant="outlined" sx={{ mb: 2 }}>
                              <CardContent>
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                  alignItems="center"
                                  sx={{ cursor: 'pointer' }}
                                  onClick={() => toggleCampaignExpand(campaign.campaign_id)}
                                >
                                  <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
                                    <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main }}>
                                      <CampaignIcon />
                                    </Avatar>
                                    <Box sx={{ flex: 1 }}>
                                      <Typography variant="subtitle1" fontWeight={600}>
                                        {campaign.campaign_name}
                                      </Typography>
                                      <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                          <DateRangeIcon sx={{ fontSize: 14 }} />
                                          {formatDate(campaign.start_date)} - {formatDate(campaign.end_date)}
                                        </Typography>
                                      </Stack>
                                    </Box>
                                  </Stack>
                                  <Stack direction="row" spacing={2} alignItems="center">
                                    <Box sx={{ textAlign: 'right' }}>
                                      <Typography variant="body2" fontWeight={600} color="primary.main">
                                        {campaignRedemptionPercentage}% Redeemed
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {campaign.total_vouchers} total vouchers
                                      </Typography>
                                    </Box>
                                    {expandedCampaign === campaign.campaign_id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                  </Stack>
                                </Stack>

                                <Collapse in={expandedCampaign === campaign.campaign_id}>
                                  <Box sx={{ mt: 3, pl: { xs: 0, sm: 7 } }}>
                                    <TableContainer component={Paper} variant="outlined">
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow>
                                            <TableCell>Metric</TableCell>
                                            <TableCell align="right">Value</TableCell>
                                            <TableCell align="right">Percentage</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          <TableRow>
                                            <TableCell>Total Vouchers</TableCell>
                                            <TableCell align="right">{campaign.total_vouchers}</TableCell>
                                            <TableCell align="right">100%</TableCell>
                                          </TableRow>
                                          <TableRow>
                                            <TableCell>Redeemed Vouchers</TableCell>
                                            <TableCell align="right" sx={{ color: 'success.main', fontWeight: 600 }}>
                                              {campaign.used_vouchers || 0}
                                            </TableCell>
                                            <TableCell align="right" sx={{ color: 'success.main' }}>
                                              {campaign.total_vouchers > 0
                                                ? `${((campaign.used_vouchers || 0) / campaign.total_vouchers * 100).toFixed(1)}%`
                                                : '0%'}
                                            </TableCell>
                                          </TableRow>
                                          <TableRow>
                                            <TableCell>Unused Vouchers</TableCell>
                                            <TableCell align="right" sx={{ color: 'warning.main' }}>
                                              {campaign.remaining_vouchers || 0}
                                            </TableCell>
                                            <TableCell align="right" sx={{ color: 'warning.main' }}>
                                              {campaign.total_vouchers > 0
                                                ? `${((campaign.remaining_vouchers || 0) / campaign.total_vouchers * 100).toFixed(1)}%`
                                                : '0%'}
                                            </TableCell>
                                          </TableRow>
                                          <TableRow>
                                            <TableCell>Total Spent</TableCell>
                                            <TableCell align="right" colSpan={2} sx={{ color: 'primary.main', fontWeight: 600 }}>
                                              {formatAmount(campaign.total_payments)}
                                            </TableCell>
                                          </TableRow>
                                        </TableBody>
                                      </Table>
                                    </TableContainer>
                                  </Box>
                                </Collapse>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </>
                    )}
                  </TabPanel>

                  {/* Payment Ledger Tab */}
                  <TabPanel value={tabValue} index={2}>
                    {loading.ledger ? (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : paymentLedger.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <LedgerIcon sx={{ fontSize: 48, color: theme.palette.grey[300], mb: 2 }} />
                        <Typography color="text.secondary">
                          No ledger entries found
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        {/* Summary Cards */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                          <Grid item xs={12} sm={6} md={3}>
                            <Card sx={{ bgcolor: alpha(theme.palette.success.main, 0.05) }}>
                              <CardContent>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.main }}>
                                    <AccountBalanceIcon />
                                  </Avatar>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">
                                      Total DR (Credit)
                                    </Typography>
                                    <Typography variant="h6" fontWeight={700} color="success.main">
                                      {formatAmount(paymentLedger.reduce((sum, entry) => {
                                        const drValue = parseFloat(entry.drValue) || 0;
                                        return sum + drValue;
                                      }, 0))}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </CardContent>
                            </Card>
                          </Grid>

                          <Grid item xs={12} sm={6} md={3}>
                            <Card sx={{ bgcolor: alpha(theme.palette.error.main, 0.05) }}>
                              <CardContent>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Avatar sx={{ bgcolor: alpha(theme.palette.error.main, 0.1), color: theme.palette.error.main }}>
                                    <PaymentIcon />
                                  </Avatar>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">
                                      Total CR (Debit)
                                    </Typography>
                                    <Typography variant="h6" fontWeight={700} color="error.main">
                                      {formatAmount(paymentLedger.reduce((sum, entry) => {
                                        const crValue = parseFloat(entry.crValue) || 0;
                                        return sum + crValue;
                                      }, 0))}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </CardContent>
                            </Card>
                          </Grid>

                          <Grid item xs={12} sm={6} md={3}>
                            <Card sx={{ bgcolor: alpha(theme.palette.info.main, 0.05) }}>
                              <CardContent>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main }}>
                                    <AccountBalanceIcon />
                                  </Avatar>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">
                                      Net Amount
                                    </Typography>
                                    <Typography variant="h6" fontWeight={700} color="info.main">
                                      {formatAmount(paymentLedger.reduce((sum, entry) => sum + (Number(entry.amount) || 0), 0))}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </CardContent>
                            </Card>
                          </Grid>

                          <Grid item xs={12} sm={6} md={3}>
                            <Card sx={{ bgcolor: alpha(theme.palette.warning.main, 0.05) }}>
                              <CardContent>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.main }}>
                                    <ReceiptIcon />
                                  </Avatar>
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">
                                      Total Transactions
                                    </Typography>
                                    <Typography variant="h6" fontWeight={700}>
                                      {paymentLedger.length}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </CardContent>
                            </Card>
                          </Grid>
                        </Grid>

                        {/* Ledger Table */}
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell>Wallet ID</TableCell>
                                <TableCell>Campaign</TableCell>
                                <TableCell>Shop ID</TableCell>
                                <TableCell align="right">DR (Credit)</TableCell>
                                <TableCell align="right">CR (Debit)</TableCell>
                                <TableCell align="right">Amount</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {paymentLedger.map((entry) => {
                                const drValue = parseFloat(entry.drValue) || 0;
                                const crValue = parseFloat(entry.crValue) || 0;

                                return (
                                  <TableRow key={entry.id} hover>
                                    <TableCell>
                                      <Tooltip title={formatDateTime(entry.dateCreated)}>
                                        <Typography variant="body2">
                                          {formatDate(entry.dateCreated)}
                                        </Typography>
                                      </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          maxWidth: 250,
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap'
                                        }}
                                      >
                                        {entry.desp || 'N/A'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.75rem' }}>
                                        {entry.walletID}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2">
                                        {entry.campaign_name || 'N/A'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="body2">
                                        {entry.shopID || 'N/A'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      {drValue > 0 ? (
                                        <Typography variant="body2" fontWeight={600} color="success.main">
                                          {formatAmount(drValue)}
                                        </Typography>
                                      ) : (
                                        <Typography variant="body2" color="text.disabled">
                                          -
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell align="right">
                                      {crValue > 0 ? (
                                        <Typography variant="body2" fontWeight={600} color="error.main">
                                          {formatAmount(crValue)}
                                        </Typography>
                                      ) : (
                                        <Typography variant="body2" color="text.disabled">
                                          -
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography
                                        variant="body2"
                                        fontWeight={600}
                                        color={entry.amount > 0 ? 'success.main' : entry.amount < 0 ? 'error.main' : 'text.primary'}
                                      >
                                        {formatAmount(entry.amount)}
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}

                              {/* Totals Row */}
                              <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                                <TableCell colSpan={5}>
                                  <Typography variant="subtitle2" fontWeight={700}>
                                    TOTALS
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="subtitle2" fontWeight={700} color="success.main">
                                    {formatAmount(paymentLedger.reduce((sum, entry) => {
                                      const drValue = parseFloat(entry.drValue) || 0;
                                      return sum + drValue;
                                    }, 0))}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="subtitle2" fontWeight={700} color="error.main">
                                    {formatAmount(paymentLedger.reduce((sum, entry) => {
                                      const crValue = parseFloat(entry.crValue) || 0;
                                      return sum + crValue;
                                    }, 0))}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="subtitle2" fontWeight={700} color="info.main">
                                    {formatAmount(paymentLedger.reduce((sum, entry) => sum + (Number(entry.amount) || 0), 0))}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </>
                    )}
                  </TabPanel>
                </Paper>
              </>
            )}

            {/* No Donor Selected State */}
            {!selectedDonorId && !loading.donors && (
              <Paper sx={{ py: 8, textAlign: 'center' }}>
                <Stack spacing={2} alignItems="center">
                  <Avatar sx={{ width: 80, height: 80, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                    <DonorIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
                  </Avatar>
                  <Typography variant="h6" color="text.secondary">
                    Select a Donor
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Choose a donor from the dropdown above to view their details
                  </Typography>
                </Stack>
              </Paper>
            )}

            {/* Refresh Indicator */}
            {refreshing && (
              <Box
                sx={{
                  position: 'fixed',
                  top: 80,
                  right: 24,
                  zIndex: 1100
                }}
              >
                <Paper sx={{ p: 1, bgcolor: alpha(theme.palette.primary.main, 0.9), color: 'white' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={16} color="inherit" />
                    <Typography variant="caption">Refreshing...</Typography>
                  </Stack>
                </Paper>
              </Box>
            )}

            {/* Snackbar */}
            <Snackbar
              open={snackbar.open}
              autoHideDuration={4000}
              onClose={handleCloseSnackbar}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
                {snackbar.message}
              </Alert>
            </Snackbar>

          </Container>
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default DonorReport;